import axios, {isAxiosError} from 'axios';
import {create} from 'zustand';
import {devtools} from 'zustand/middleware';

import {login} from './api';
import type {Organization, User} from './types';

import identify from 'analytics/identify';
import {
    AUTH_TOKEN_REFRESH_INTERVAL,
    IDLE_EXPIRE_AFTER,
    IDLE_WARN_AFTER,
} from 'common/globals';

export const ALL_OPTION = {
    active: false,
    id: 0,
    isDefault: false,
    name: 'All',
};

function addAllOptionToCenters(organizations: Organization[]) {
    // if no organizations are marked as active, make the first one active
    if (!organizations.some(org => org.active === true)) {
        organizations[0].active = true;
    }

    const newOrganizations = organizations.map(organization => {
        const isActive = organization.active;

        // add the "All" option to the centers array as the first option
        organization.centers.unshift(ALL_OPTION);

        // if its the active organization but no center is marked as active, set "All" to active
        if (
            isActive &&
            !organization.centers.some(center => center.active === true)
        ) {
            organization.centers[0].active = true;
        }

        return organization;
    });

    return newOrganizations;
}

function deriveActives(organizations: Organization[]) {
    const activeOrg = organizations.find(org => org.active === true);

    return {
        activeCenter: activeOrg?.centers.find(center => center.active === true),
        activeOrg,
    };
}

export const useUserStore = create<User>()(
    devtools(set => ({
        activeCenter: {
            active: true,
            id: 0,
            isDefault: false,
            name: '',
        },
        activeOrg: {
            active: true,
            brand: {
                isNavBackgroundLight: false,
                logoUrl: null,
                navBackgroundColor: null,
            },
            centers: [],
            features: {
                addPatient: false,
                cdiAlerts: false,
                documentStorage: false,
                drFirstNotifications: false,
                eFaxing: false,
                enableImprovedNameParsing: false,
                encounterReviews: false,
                hraV2: false,
                supportsPatientRiskProfile: false,
            },
            id: 0,
            isSsoEnabled: false,
            isUserDefault: false,
            name: '',
            navigation: [],
            planType: 'ISNP',
            timezone: 'America/New_York',
        },
        email: '',
        environment: {
            authRefreshInterval: AUTH_TOKEN_REFRESH_INTERVAL,
            idleExpireAfter: IDLE_EXPIRE_AFTER,
            idleWarnAfter: IDLE_WARN_AFTER,
        },
        fetch: async () => {
            try {
                const {
                    data: {data},
                }: {data: {data: User}} = await axios.get('users/me/');
                const {id, email, firstName, lastName, organizations} = data;
                const orgs = addAllOptionToCenters(organizations);

                identify({email, firstName, id, lastName});
                set({
                    ...data,
                    organizations: orgs,
                    ...deriveActives(orgs),
                });
            } catch (error: unknown) {
                if (
                    isAxiosError(error) &&
                    (error.response?.status === 401 ||
                        error.response?.status === 403)
                ) {
                    // user isn't logged in so redirect to the login page
                    return login();
                }

                throw error;
            }
        },
        firstName: '',
        hasIntegrationMonitoring: false,
        id: null,
        isDocumentTypeManager: false,
        isEncounterReviewer: false,
        isHedisValuesManager: false,
        isManager: false,
        isPatientTagManager: false,
        isReadOnly: false,
        isStaff: false,
        isSuperManager: false,
        lastName: '',
        organizations: [],
        setActive: (orgId, centerId) => {
            set(state => {
                const organizations = state.organizations.map(org => {
                    const isActiveOrg = org.id === orgId;

                    return {
                        ...org,
                        active: isActiveOrg,
                        centers: org.centers.map((cen, i) => ({
                            ...cen,
                            active:
                                isActiveOrg && !centerId && i === 0 ?
                                    true
                                :   cen.id === centerId,
                        })),
                    };
                });

                return {
                    ...state,
                    organizations,
                    ...deriveActives(organizations),
                };
            });
        },
        setUserDefaultOrg: (orgId: number | null) => {
            set(state => {
                // Mark the selected organization, if there is one, as the user's default. Mark all others as not the default.
                const organizations = state.organizations.map(org => {
                    return {
                        ...org,
                        isUserDefault: org.id === orgId,
                    };
                });

                return {
                    ...state,
                    organizations,
                };
            });
        },
        settings: {
            useHybridUi: false,
        },
    })),
);
